import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  Button,
  Card,
  Text,
  Flex,
  Box,
  Input,
  Checkbox,
} from '../../design-system';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalFooter,
} from '../../design-system';
import {
  fetchGoogleContacts,
  syncSelectedContacts,
} from '../../helpers/googleContacts';
import Alert from '../../helpers/alert';
import { ERR_MSG } from '../../globalConstants';

const ModalContentWrapper = styled.div`
  max-height: 80vh;
  overflow-y: auto;
`;

const ContactCard = styled(Card)<{ selected?: boolean }>`
  margin-bottom: 12px;
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;

  &:hover {
    border-color: #007bff;
    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.1);
  }

  ${(props) =>
    props.selected &&
    `
    border-color: #007bff;
    background-color: #f8f9ff;
  `}
`;

const StatusBadge = styled(Box)<{ status?: string }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${(props) => {
    switch (props.status) {
      case 'new':
        return `
          background-color: #d4edda;
          color: #155724;
        `;
      case 'will_update':
        return `
          background-color: #fff3cd;
          color: #856404;
        `;
      default:
        return `
          background-color: #f8f9fa;
          color: #6c757d;
        `;
    }
  }}
`;

const SearchInput = styled(Input)`
  margin-bottom: 16px;
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: self-start;
  gap: 12px;
`;

const ContactDetails = styled.div`
  flex: 1;
`;

const ContactName = styled(Text)`
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 4px;
`;

const ContactMeta = styled(Text)`
  color: #6c757d;
  font-size: 14px;
  margin-bottom: 2px;
  display: block;
`;

const SelectionSummary = styled.div`
  position: sticky;
  bottom: 0;
  background: white;
  border-top: 1px solid #e0e0e0;
  padding: 16px;
  margin: 0 -24px -24px -24px;
`;

interface GoogleContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSyncComplete?: () => void;
}

const GoogleContactsModal = ({
  isOpen,
  onClose,
  onSyncComplete,
}: GoogleContactsModalProps) => {
  const [contacts, setContacts] = useState<any[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<any[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<Set<any>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadContacts();
    }
  }, [isOpen]);

  useEffect(() => {
    filterContacts();
  }, [contacts, searchTerm]);

  useEffect(() => {
    setSelectAll(
      selectedContacts.size === filteredContacts.length &&
        filteredContacts.length > 0,
    );
  }, [selectedContacts, filteredContacts]);

  const loadContacts = async () => {
    setLoading(true);
    try {
      const response = await fetchGoogleContacts();
      setContacts(response.contacts || []);
    } catch (error) {
      Alert.error(ERR_MSG);
      console.error('Failed to load Google contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterContacts = () => {
    if (!searchTerm.trim()) {
      setFilteredContacts(contacts);
      return;
    }

    const filtered = contacts.filter((contact) => {
      const searchLower = searchTerm.toLowerCase();
      const name = `${contact.first_name || ''} ${
        contact.last_name || ''
      }`.toLowerCase();
      const emails = contact.emails?.map((e) => e.value).join(' ') || '';
      const phones = contact.phones?.map((p) => p.value).join(' ') || '';
      const company = contact.company || '';

      return (
        name.includes(searchLower) ||
        emails.includes(searchLower) ||
        phones.includes(searchLower) ||
        company.toLowerCase().includes(searchLower)
      );
    });

    setFilteredContacts(filtered);
  };

  const handleContactSelect = (contactId: any) => {
    const newSelected = new Set(selectedContacts);
    if (newSelected.has(contactId)) {
      newSelected.delete(contactId);
    } else {
      newSelected.add(contactId);
    }
    setSelectedContacts(newSelected);
  };

  const handleSelectAll = (e: any) => {
    if (e && e.target) {
      const checked = e.target.checked;
      if (checked) {
        const allIds = new Set(
          filteredContacts.map((c) => c.google_contact_id),
        );
        setSelectedContacts(allIds);
      } else {
        setSelectedContacts(new Set());
      }
      setSelectAll(checked);
    }
  };

  const handleSync = async () => {
    if (selectedContacts.size === 0) {
      Alert.error('Please select at least one contact to sync');
      return;
    }

    setSyncing(true);
    try {
      const response = await syncSelectedContacts(Array.from(selectedContacts));

      const { created, updated, skipped } = response.results;
      let message = `Sync completed: ${created} created, ${updated} updated`;
      if (skipped > 0) {
        message += `, ${skipped} skipped`;
      }

      Alert.success(message);
      onSyncComplete?.();
      onClose();
    } catch (error) {
      Alert.error(ERR_MSG);
      console.error('Failed to sync contacts:', error);
    } finally {
      setSyncing(false);
    }
  };

  const getStatusBadge = (contact: any) => {
    if (contact.match_status?.status === 'will_update') {
      return (
        <StatusBadge status="will_update">
          Will Update: {contact.match_status.existing_contact.name}
        </StatusBadge>
      );
    }
    return <StatusBadge status="new">New</StatusBadge>;
  };

  const formatContactInfo = (contact: any) => {
    const emails = contact.emails?.map((e) => e.value).join(', ') || 'No email';
    const phones = contact.phones?.map((p) => p.value).join(', ') || 'No phone';
    const company = contact.company || 'No company';
    const title = contact.title || '';

    return { emails, phones, company, title };
  };

  return (
    <Modal show={isOpen} onClose={onClose} size="lg">
      <ModalHeader>
        <Text fontSize={24} fontWeight={700}>
          Import Google Contacts
        </Text>
      </ModalHeader>

      <ModalContent>
        <ModalContentWrapper>
          <input
            type="text"
            name="search-contacts"
            placeholder="Search contacts by name, email, phone, or company..."
            value={searchTerm}
            onChange={(e) => {
              console.log('Search input changed:', e);
              setSearchTerm(e.target.value);
            }}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              marginBottom: '16px',
              fontSize: '14px',
            }}
          />

          <Flex justifyContent="space-between" alignItems="center" mb={16}>
            <Text fontSize={16} fontWeight={600}>
              {filteredContacts.length} contact
              {filteredContacts.length !== 1 ? 's' : ''} found
            </Text>
            <Checkbox
              id="select-all"
              name="select-all"
              checked={selectAll}
              onChange={handleSelectAll}
              label="Select All"
            />
          </Flex>

          {loading ? (
            <Text textAlign="center" py={40}>
              Loading Google contacts...
            </Text>
          ) : (
            <div>
              {filteredContacts.map((contact) => {
                const isSelected = selectedContacts.has(
                  contact.google_contact_id,
                );
                const { emails, phones, company, title } =
                  formatContactInfo(contact);

                return (
                  <ContactCard
                    key={contact.google_contact_id}
                    selected={isSelected}
                    p={16}
                  >
                    <ContactInfo>
                      <Checkbox
                        id={`contact-${contact.google_contact_id}`}
                        name={`contact-${contact.google_contact_id}`}
                        checked={isSelected}
                        onChange={(e) => {
                          if (e && typeof e.stopPropagation === 'function') {
                            e.stopPropagation();
                          }
                          handleContactSelect(contact.google_contact_id);
                        }}
                      />
                      <ContactDetails>
                        <ContactName
                          style={{ fontSize: '16px', fontWeight: 600 }}
                        >
                          {contact.first_name} {contact.last_name}
                        </ContactName>
                        <ContactMeta>{emails}</ContactMeta>
                        <ContactMeta>{phones}</ContactMeta>
                        <ContactMeta>
                          {company} {title && `• ${title}`}
                        </ContactMeta>
                      </ContactDetails>
                      {getStatusBadge(contact)}
                    </ContactInfo>
                  </ContactCard>
                );
              })}
            </div>
          )}
        </ModalContentWrapper>
      </ModalContent>

      <ModalFooter>
        <SelectionSummary>
          <Flex justifyContent="space-between" alignItems="center">
            <Text fontSize={16} fontWeight={600}>
              {selectedContacts.size} contact
              {selectedContacts.size !== 1 ? 's' : ''} selected
            </Text>
            <Flex gap={12}>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={handleSync}
                loading={syncing}
                disabled={selectedContacts.size === 0}
              >
                Import Selected
              </Button>
            </Flex>
          </Flex>
        </SelectionSummary>
      </ModalFooter>
    </Modal>
  );
};

export default GoogleContactsModal;
